import { get } from 'svelte/store';
import { format } from 'svelte-i18n';

import { user_data, user_settings } from 'src/stores/account/user';
import {
	currentUnitId,
	recoverFromRevokedAccess,
	refreshHomes,
	showLostHomeToast,
	sub_units,
} from 'src/stores/account/home';
import { forceLogout } from 'src/services/auth_v2';
import { refreshRightsScopedData } from 'src/services/http';

import type { SubUnitData, SubUnitRights } from 'src/types/SubUnit';

/**
 * Housing socket events (FE-2022).
 *
 * The backend pushes account/home lifecycle changes over the notifications
 * socket wrapped as `{ event: "api2ui", ul: [<unit_id>], data: {...} }`, on the
 * same channel as the generic list-sync. This module handles the `data.changed`
 * discriminators that concern homes, shared access, ownership and account
 * removal; everything else keeps flowing through `on_update`.
 *
 * Captured payload shapes:
 * - `{ changed: "SUB",           deleted: <sub_id> }`
 * - `{ changed: "SUB_RIGHTS",    sub: <sub_id>, data: <rights tuple> }`
 * - `{ changed: "UNIT",          deleted: <unit_id> }`
 * - `{ changed: "UNIT_OWNER",    unit_id: <id>, new_owner: <email> }`
 * - `{ changed: "ACCOUNT_REMOVED" }`
 *
 * Ids arrive as numbers; unit ids are compared against the string
 * `currentUnitId`, so they are `String()`-normalized before comparison.
 */

export type HousingChangedType = 'SUB' | 'SUB_RIGHTS' | 'UNIT' | 'UNIT_OWNER' | 'ACCOUNT_REMOVED';

export interface HousingSocketData {
	changed: HousingChangedType;
	/** SUB: removed sub id. UNIT: deleted unit id. */
	deleted?: number;
	/** SUB_RIGHTS: sub id whose rights changed. */
	sub?: number;
	/** SUB_RIGHTS: full home-level rights tuple. */
	data?: SubUnitRights;
	/** UNIT_OWNER: unit whose ownership moved. */
	unit_id?: number;
	/** UNIT_OWNER: email of the new owner. */
	new_owner?: string;
}

const HOUSING_CHANGED_TYPES: ReadonlySet<string> = new Set<HousingChangedType>([
	'SUB',
	'SUB_RIGHTS',
	'UNIT',
	'UNIT_OWNER',
	'ACCOUNT_REMOVED',
]);

export const isHousingSocketEvent = (data: unknown): data is HousingSocketData =>
	!!data &&
	typeof data === 'object' &&
	HOUSING_CHANGED_TYPES.has((data as { changed?: unknown }).changed as string);

/** Current session's sub id, or undefined when not in a shared home. */
const currentSubId = (): number | undefined => {
	const sub = get(user_settings)?.sub;
	return typeof sub === 'number' ? sub : undefined;
};

const handleSubRemoved = async (data: HousingSocketData): Promise<void> => {
	const mySub = currentSubId();
	if (mySub !== undefined && Number(data.deleted) === mySub) {
		await recoverFromRevokedAccess();
		return;
	}
	// Owner side: drop the removed shared user so an open Sharing screen updates.
	sub_units.remove(`sub_${data.deleted}`);
};

const handleSubRightsChanged = async (data: HousingSocketData): Promise<void> => {
	const mySub = currentSubId();
	if (mySub !== undefined && Number(data.sub) === mySub) {
		// My rights changed — re-fetch rights-scoped data so device/scene controls
		await refreshRightsScopedData();
		return;
	}
	// Owner side: patch the shared user's rights in place so an open Sharing
	// screen reflects the new permissions without a re-fetch.
	if (Array.isArray(data.data)) {
		const key = `sub_${data.sub}`;
		const current = get(sub_units)[key];
		if (current) {
			sub_units.update({ [key]: [current[0], ...data.data] as SubUnitData });
		}
	}
};

const handleUnitDeleted = async (data: HousingSocketData, ul?: number[]): Promise<void> => {
	const deletedId = String(data.deleted ?? ul?.[0] ?? '');
	if (!deletedId) return;

	if (deletedId === get(currentUnitId)) {
		await recoverFromRevokedAccess(() =>
			showLostHomeToast(
				get(format)('home_deleted', { default: 'Home deleted' }),
				get(format)('home_deleted_description', {
					default: 'This home has been deleted.',
				}),
			),
		);
		return;
	}
	await refreshHomes();
};

const handleUnitOwnerChanged = async (data: HousingSocketData, ul?: number[]): Promise<void> => {
	const unitId = String(data.unit_id ?? ul?.[0] ?? '');
	if (!unitId) return;

	const myEmail = get(user_data)?.email?.toLowerCase();
	const newOwner = String(data.new_owner ?? '').toLowerCase();

	// Ownership moved *to* this account — keep the home, just resync the list.
	if (newOwner && myEmail && newOwner === myEmail) {
		await refreshHomes();
		return;
	}

	// Only the previous owner loses the home. A shared member keeps their access
	const iOwnedThisUnit = Object.prototype.hasOwnProperty.call(
		get(user_data)?.available_units ?? {},
		unitId,
	);
	if (!iOwnedThisUnit) {
		await refreshHomes();
		return;
	}

	if (unitId === get(currentUnitId)) {
		await recoverFromRevokedAccess(() =>
			showLostHomeToast(
				get(format)('home_transferred', { default: 'Home transferred' }),
				get(format)('home_transferred_description', {
					default: 'This home now belongs to another account.',
				}),
			),
		);
		return;
	}
	await refreshHomes();
};

const handleAccountRemoved = async (): Promise<void> => {
	await forceLogout(() =>
		showLostHomeToast(
			get(format)('account_removed', { default: 'Account removed' }),
			get(format)('account_removed_description', {
				default: 'This account is no longer available.',
			}),
		),
	);
};

export const handleHousingSocketEvent = async (
	data: HousingSocketData,
	ul?: number[],
): Promise<void> => {
	try {
		switch (data.changed) {
			case 'SUB':
				await handleSubRemoved(data);
				break;
			case 'SUB_RIGHTS':
				await handleSubRightsChanged(data);
				break;
			case 'UNIT':
				await handleUnitDeleted(data, ul);
				break;
			case 'UNIT_OWNER':
				await handleUnitOwnerChanged(data, ul);
				break;
			case 'ACCOUNT_REMOVED':
				await handleAccountRemoved();
				break;
		}
	} catch (error) {
		console.error('[housing] failed to handle socket event', data?.changed, error);
	}
};
